import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, ArrowDown, Edit2, Trash2, Archive, ChevronsUpDown } from 'lucide-react';
import unitsService from '../../services/unitsService';
import unitTypesService from '../../services/unitTypesService';
import rentalsService from '../../services/rentalsService';
import customersService from '../../services/customersService';
import { UNIT_STATUS_PILL } from '../../utils/unitStatusColors';

/**
 * Dense table view of units. ESS-parity with their "List View" — operator
 * sees every unit in a sortable table with tenant info joined in. Sits
 * alongside the visual Grid / Site Map / Unit Types tabs.
 *
 * Joins are done client-side from three already-cached endpoints:
 *   - /units                  -> base unit roster
 *   - /unit-types             -> human-readable type names (denormalized
 *                                 onto unit.unit_type already, but the
 *                                 size string lives on the type doc)
 *   - /rentals                -> active tenant + move-in-date per unit
 *
 * No new backend endpoint needed; this is purely a presentational layer.
 */

const STATUS_PILL = UNIT_STATUS_PILL;

const SortHeader = ({ label, sortKey, sortState, onSort, align = 'left' }) => {
  const active = sortState.key === sortKey;
  const Icon = !active
    ? ChevronsUpDown
    : sortState.dir === 'asc'
      ? ArrowUp
      : ArrowDown;
  return (
    <th
      className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-slate-900 ${active ? 'text-slate-900' : ''}`}
      >
        {label}
        <Icon className="h-3 w-3" />
      </button>
    </th>
  );
};

const UnitsListView = ({ facilityId }) => {
  const [units, setUnits] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortState, setSortState] = useState({ key: 'unit_number', dir: 'asc' });
  const [deleting, setDeleting] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  // Unit-status filter pill. 'all' shows every status; the rest match the
  // unit.status value rendered in the Status column. Filters client-side.
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const [unitsRes, typesRes, rentalsRes, customersRes] = await Promise.all([
        unitsService.getAll(facilityId, showArchived ? { include_archived: true } : undefined),
        unitTypesService.getAll(facilityId),
        rentalsService.getAll(facilityId),
        customersService.getAll(facilityId, { status: 'all', limit: 5000 }),
      ]);
      setUnits(unitsRes.data || []);
      setUnitTypes(typesRes.data || []);
      setRentals(rentalsRes.data || []);
      setCustomers(customersRes.data || []);
    } catch (err) {
      console.error('UnitsListView load error:', err);
      setError('Could not load units.');
    } finally {
      setLoading(false);
    }
  }, [facilityId, showArchived]);

  useEffect(() => {
    load();
  }, [load]);

  // Index helpers.
  const typeById = useMemo(() => {
    const m = new Map();
    for (const t of unitTypes) m.set(t._id, t);
    return m;
  }, [unitTypes]);

  const customerById = useMemo(() => {
    const m = new Map();
    for (const c of customers) m.set(c._id, c);
    return m;
  }, [customers]);

  // unit_id -> active rental. Active = not Ended (so Delinquent / Lien
  // still count as the current rental for tenant-name display).
  const activeRentalByUnit = useMemo(() => {
    const m = new Map();
    for (const r of rentals) {
      const status = (r.status || '').toLowerCase();
      if (status === 'ended' || r.end_date) continue;
      if (r.unit_id) m.set(r.unit_id, r);
    }
    return m;
  }, [rentals]);

  // Compose a denormalized row per unit. Skip archived.
  const rows = useMemo(() => {
    return units
      .filter((u) => showArchived || !u.is_archived)
      .map((u) => {
        const t = u.unit_type_id ? typeById.get(u.unit_type_id) : null;
        const typeName = (t && t.name) || u.unit_type || 'Unclassified';
        const size =
          t && t.width != null && t.length != null
            ? `${t.width}x${t.length}`
            : u.width != null && u.length != null
              ? `${u.width}x${u.length}`
              : null;
        const rate =
          u.rate_override != null
            ? u.rate_override
            : t && t.monthly_rate != null
              ? t.monthly_rate
              : u.monthly_rate;
        const rental = activeRentalByUnit.get(u._id);
        const customer = rental && rental.customer_id
          ? customerById.get(rental.customer_id)
          : null;
        const tenantName = customer
          ? customer.full_name
              || [customer.first_name, customer.last_name].filter(Boolean).join(' ')
              || customer.email
          : null;
        // Customer-level gate code (PTI model — one PIN per human).
        const gateCode = customer?.gate_code || null;
        return {
          _id: u._id,
          unit_number: u.unit_number,
          type_name: typeName,
          size,
          status: u.status,
          rate: rate != null ? Number(rate) : null,
          tenant_name: tenantName,
          tenant_id: customer?._id || null,
          move_in: rental?.start_date || null,
          gate_code: gateCode,
          is_archived: !!u.is_archived,
        };
      });
  }, [units, typeById, activeRentalByUnit, customerById, showArchived]);

  // Sort. Numeric-aware for unit_number ("P3" < "P10") + rate.
  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const { key, dir } = sortState;
    const sign = dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // nulls last regardless of dir
      if (bv == null) return -1;
      if (key === 'unit_number') {
        // natural sort
        return (
          sign *
          String(av).localeCompare(String(bv), undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        );
      }
      if (typeof av === 'number' && typeof bv === 'number') {
        return sign * (av - bv);
      }
      return sign * String(av).localeCompare(String(bv));
    });
    return arr;
  }, [rows, sortState]);

  const onSort = (key) => {
    setSortState((s) => {
      if (s.key === key) return { key, dir: s.dir === 'asc' ? 'desc' : 'asc' };
      return { key, dir: 'asc' };
    });
  };

  // Status filter pills. Always offer the canonical statuses; additionally
  // surface Delinquent / Overdue only if they actually appear in the data.
  const statusOptions = useMemo(() => {
    const base = ['Available', 'Occupied', 'Reserved', 'Maintenance'];
    const present = new Set(rows.map((r) => r.status).filter(Boolean));
    for (const extra of ['Delinquent', 'Overdue']) {
      if (present.has(extra)) base.push(extra);
    }
    return base;
  }, [rows]);

  // Apply the status filter after sorting so the sort memo is untouched.
  const visibleRows = useMemo(
    () => (statusFilter === 'all'
      ? sortedRows
      : sortedRows.filter((r) => r.status === statusFilter)),
    [sortedRows, statusFilter],
  );

  const onDelete = async (unitId, unitNumber) => {
    if (!window.confirm(`Delete unit ${unitNumber}? This cannot be undone.`)) {
      return;
    }
    setDeleting(unitId);
    try {
      await unitsService.delete(facilityId, unitId);
      setUnits((curr) => curr.filter((u) => u._id !== unitId));
    } catch (err) {
      window.alert(
        err.response?.data?.detail || 'Could not delete unit.',
      );
    } finally {
      setDeleting(null);
    }
  };

  // Soft archive / unarchive (ported from the old Manage tab). Reversible —
  // distinct from the hard Delete above.
  const onArchive = async (unitId, isArchived) => {
    try {
      if (isArchived) {
        await unitsService.unarchive(facilityId, unitId);
      } else {
        await unitsService.archive(facilityId, unitId);
      }
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Could not update unit.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-[13px] text-red-700">
        {error}
      </div>
    );
  }

  if (!sortedRows.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center">
        <h3 className="text-base font-semibold text-slate-700 mb-1">No units yet</h3>
        <p className="text-[13px] text-slate-500">
          Define your unit types and bulk-add units to populate this list.
        </p>
      </div>
    );
  }

  const fmtDate = (iso) => {
    if (!iso) return '—';
    // Date-only strings ("YYYY-MM-DD") parse as UTC midnight, which renders a
    // day EARLIER in negative-UTC zones (move-in 2026-05-20 showing 5/19 in
    // Central). Parse as local midnight so it matches the unit/rental record.
    const s = String(iso);
    const d = /^\d{4}-\d{2}-\d{2}/.test(s) && s.length === 10
      ? new Date(`${s}T00:00:00`)
      : new Date(s);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        {/* Unit-status filter pills. Filters the rows client-side. */}
        <div className="flex flex-wrap items-center gap-2">
          {[{ key: 'all', label: 'All' }, ...statusOptions.map((s) => ({ key: s, label: s }))].map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
                statusFilter === f.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-[13px] text-slate-500 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="rounded border-slate-300"
          />
          Show archived
        </label>
      </div>
      <div className="rounded-xl border border-slate-200/80 bg-white overflow-x-auto">
        <table className="min-w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <SortHeader label="Unit #" sortKey="unit_number" sortState={sortState} onSort={onSort} />
            <SortHeader label="Type" sortKey="type_name" sortState={sortState} onSort={onSort} />
            <SortHeader label="Size" sortKey="size" sortState={sortState} onSort={onSort} />
            <SortHeader label="Status" sortKey="status" sortState={sortState} onSort={onSort} />
            <SortHeader label="Rate" sortKey="rate" sortState={sortState} onSort={onSort} align="right" />
            <SortHeader label="Tenant" sortKey="tenant_name" sortState={sortState} onSort={onSort} />
            <SortHeader label="Move-in" sortKey="move_in" sortState={sortState} onSort={onSort} />
            <SortHeader label="Gate Code" sortKey="gate_code" sortState={sortState} onSort={onSort} />
            <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 text-right">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.length === 0 && (
            <tr>
              <td colSpan="9" className="px-3 py-8 text-center text-[13px] text-slate-400">
                No units match this filter
              </td>
            </tr>
          )}
          {visibleRows.map((r) => (
            <tr key={r._id} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 ${r.is_archived ? 'opacity-60' : ''}`}>
              <td className="px-3 py-2 text-[13px] font-medium text-slate-900">
                <Link to={`/units/${r._id}`} className="hover:text-blue-600">
                  {r.unit_number}
                </Link>
              </td>
              <td className="px-3 py-2 text-[13px] text-slate-700">{r.type_name}</td>
              <td className="px-3 py-2 text-[13px] text-slate-500">{r.size || '—'}</td>
              <td className="px-3 py-2">
                <span
                  className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border ${
                    STATUS_PILL[r.status] || 'bg-slate-50 text-slate-700 border-slate-100'
                  }`}
                >
                  {r.status}
                </span>
              </td>
              <td className="px-3 py-2 text-[13px] text-slate-700 text-right tabular-nums">
                {r.rate != null ? `$${r.rate.toFixed(2)}` : '—'}
              </td>
              <td className="px-3 py-2 text-[13px] text-slate-700">
                {r.tenant_id ? (
                  <Link
                    to={`/customers/${r.tenant_id}`}
                    className="hover:text-blue-600"
                  >
                    {r.tenant_name}
                  </Link>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </td>
              <td className="px-3 py-2 text-[13px] text-slate-500">{fmtDate(r.move_in)}</td>
              <td className="px-3 py-2 text-[13px] text-slate-500 tabular-nums">
                {r.gate_code || '—'}
              </td>
              <td className="px-3 py-2 text-right">
                <div className="inline-flex items-center gap-1">
                  <Link
                    to={`/units/${r._id}/edit`}
                    title="Edit unit"
                    className="p-1.5 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-900"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => onArchive(r._id, r.is_archived)}
                    title={r.is_archived ? 'Unarchive unit' : 'Archive unit'}
                    className="p-1.5 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-900"
                  >
                    <Archive className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={deleting === r._id}
                    onClick={() => onDelete(r._id, r.unit_number)}
                    title="Delete unit"
                    className="p-1.5 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-600 disabled:opacity-40"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default UnitsListView;
